/**
 * Unified Configure Forecast Component
 * Merges data analysis, preprocessing, and model configuration into a single streamlined view
 */

import { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';
import { useDataStore } from '../stores/dataStore';
import { useModelStore } from '../stores/modelStore';
import { wasmEngine } from '../lib/wasm';
import type { ACFPACFResult, StationarityTestResults } from '../types/wasm';

interface ConfigureForecastProps {
  onRunForecast: () => void;
  onBack?: () => void;
}

export function ConfigureForecast({ onRunForecast, onBack }: ConfigureForecastProps) {
  const { rawData, columnSelection, setProcessedData, setPreprocessing, preprocessing } = useDataStore();
  const { config, setConfig } = useModelStore();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(true);
  const [acfPacfData, setAcfPacfData] = useState<ACFPACFResult | null>(null);
  const [stationarityData, setStationarityData] = useState<StationarityTestResults | null>(null);
  const [descriptiveStats, setDescriptiveStats] = useState<any>(null);

  const [tsTab, setTsTab] = useState<'chart' | 'explanation'>('chart');
  const [distTab, setDistTab] = useState<'histogram' | 'stats' | 'explanation'>('histogram');
  const [stationarityTab, setStationarityTab] = useState<'results' | 'explanation'>('results');

  const [missingValueMethod, setMissingValueMethod] = useState<'none' | 'linear' | 'forward_fill' | 'backward_fill' | 'remove'>('linear');
  const [outlierHandling, setOutlierHandling] = useState<'keep' | 'cap_iqr' | 'cap_zscore' | 'remove'>('keep');
  const [appliedRecommendation, setAppliedRecommendation] = useState(false);

  const { originalData, qualityIssues } = useMemo(() => {
    if (!rawData || !columnSelection.dateColumn || !columnSelection.targetColumn) {
      return { originalData: [], qualityIssues: [] };
    }

    const rows = rawData.rows || [];
    const data = rows.map((row: any, index: number) => {
      const dateValue = row[columnSelection.dateColumn!];
      const targetValue = row[columnSelection.targetColumn!];

      return {
        index,
        date: dateValue,
        timestamp: new Date(dateValue).getTime(),
        value: typeof targetValue === 'number' ? targetValue : parseFloat(String(targetValue)),
      };
    }).filter((d: any) => !isNaN(d.timestamp));

    // Detect quality issues
    const issues: any[] = [];
    const missingIndices = data.map((d: any, i: number) => (isNaN(d.value) ? i : -1)).filter((i: number) => i >= 0);
    if (missingIndices.length > 0) {
      issues.push({
        type: 'missing',
        count: missingIndices.length,
        percentage: (missingIndices.length / data.length) * 100,
      });
    }

    return { originalData: data, qualityIssues: issues };
  }, [rawData, columnSelection]);

  useEffect(() => {
    runStatisticalAnalysis();
  }, []);

  const runStatisticalAnalysis = async () => {
    if (!rawData || !columnSelection.targetColumn) return;

    setIsAnalyzing(true);
    try {
      const values = rawData.rows.map((row: any) => {
        const val = row[columnSelection.targetColumn!];
        return typeof val === 'number' ? val : parseFloat(String(val));
      }).filter((v: number) => !isNaN(v));

      // Calculate descriptive statistics
      const sorted = [...values].sort((a, b) => a - b);
      const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
      const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
      const stdDev = Math.sqrt(variance);
      const min = sorted[0];
      const max = sorted[sorted.length - 1];
      const median = sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];
      const q1 = sorted[Math.floor(sorted.length * 0.25)];
      const q3 = sorted[Math.floor(sorted.length * 0.75)];
      const iqr = q3 - q1;
      const cv = (stdDev / mean) * 100;

      // Create histogram bins
      const binCount = 20;
      const binSize = max === min ? 1 : (max - min) / binCount;
      const histogram = Array(binCount).fill(0);

      values.forEach(v => {
        const binIndex = max === min ? 0 : Math.min(Math.floor((v - min) / binSize), binCount - 1);
        histogram[binIndex]++;
      });

      setDescriptiveStats({
        mean,
        median,
        stdDev,
        variance,
        min,
        max,
        q1,
        q3,
        iqr,
        cv,
        count: values.length,
        histogram,
        binSize,
      });

      const [acfPacf, stationarity] = await Promise.all([
        wasmEngine.calculateACFPACF(values).catch(() => null),
        wasmEngine.runStationarityTests(values).catch(() => null),
      ]);

      setAcfPacfData(acfPacf);
      setStationarityData(stationarity);
    } catch (error) {
      console.error('[ConfigureForecast] Error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Generate intelligent model recommendation
  const recommendation = useMemo(() => {
    const isStationary = stationarityData?.adf.p_value !== undefined && stationarityData.adf.p_value < 0.05;
    const hasSeasonality = acfPacfData !== null;
    const hasTrend = !isStationary;

    if (hasSeasonality && hasTrend) {
      return {
        model: 'exponential_smoothing' as const,
        reason: 'Data shows both trend and seasonality. Triple Exponential Smoothing (Holt-Winters) handles both effectively.',
        confidence: 'high' as const,
      };
    }

    if (isStationary) {
      return {
        model: 'arima' as const,
        reason: 'Data is stationary. ARIMA with low order parameters (1,0,1) is recommended.',
        confidence: 'high' as const,
      };
    }

    if (hasTrend && !hasSeasonality) {
      return {
        model: 'exponential_smoothing' as const,
        reason: 'Data shows trend without seasonality. Double Exponential Smoothing (Holt) is suitable.',
        confidence: 'high' as const,
      };
    }

    return {
      model: 'arima' as const,
      reason: 'Data is non-stationary. ARIMA with differencing (1,1,1) is recommended.',
      confidence: 'medium' as const,
    };
  }, [stationarityData, acfPacfData]);

  const handleRunForecast = async () => {
    const timestamps = originalData.map((d: any) => d.timestamp);
    const values = originalData.map((d: any) => d.value);

    let processedTimestamps = timestamps;
    let processedValues = values;
    const appliedChanges: string[] = [];

    // Apply preprocessing if needed
    if (qualityIssues.length > 0 && (missingValueMethod !== 'none' || outlierHandling !== 'keep')) {
      try {
        const result = await wasmEngine.preprocessData(
          timestamps,
          values,
          false,
          {
            missing_value_method: missingValueMethod,
            handle_outliers: outlierHandling !== 'keep',
            outlier_method: outlierHandling === 'cap_iqr' ? 'iqr' : 'zscore',
            iqr_multiplier: 1.5,
          }
        );

        processedTimestamps = result.timestamps;
        processedValues = result.values;

        if (missingValueMethod !== 'none') {
          appliedChanges.push(`Missing value imputation: ${missingValueMethod}`);
        }
        if (outlierHandling !== 'keep') {
          appliedChanges.push(`Outlier handling: ${outlierHandling}`);
        }
      } catch (error) {
        console.error('[ConfigureForecast] Preprocessing error:', error);
      }
    }

    setProcessedData({
      timestamps: processedTimestamps,
      values: processedValues
    });

    const isStationary = stationarityData?.adf.p_value !== undefined && stationarityData.adf.p_value < 0.05;
    const hasSeasonality = acfPacfData !== null;
    const hasTrend = !isStationary;

    setPreprocessing({
      appliedChanges,
      statisticalAnalysis: {
        isStationary,
        hasSeasonality,
        hasTrend,
      },
    });

    onRunForecast();
  };

  const handleExplainWithAI = (section: string) => {
    alert(`Want automated AI explanations for ${section}? Request this feature in the feedback form at the bottom of the page!`);
    document.getElementById('feedback-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const hasNonPositiveValues = useMemo(() => {
    return originalData.some((d: any) => d.value <= 0);
  }, [originalData]);

  const hasNegativeValues = useMemo(() => {
    return originalData.some((d: any) => d.value < 0);
  }, [originalData]);

  const histogramChartData = useMemo(() => {
    if (!descriptiveStats) return [];

    return descriptiveStats.histogram.map((count: number, i: number) => {
      const start = descriptiveStats.min + i * descriptiveStats.binSize;
      const end = descriptiveStats.max === descriptiveStats.min
        ? descriptiveStats.max
        : descriptiveStats.min + (i + 1) * descriptiveStats.binSize;
      const range = descriptiveStats.max === descriptiveStats.min
        ? start.toFixed(1)
        : `${start.toFixed(1)}-${end.toFixed(1)}`;

      return {
        range,
        count,
      };
    });
  }, [descriptiveStats]);

  return (
    <div className="space-y-6">
      {/* Card 1: Data Diagnostics & Preprocessing */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden transition-all duration-300">
        {/* Header */}
        <div className="bg-white border-b border-gray-150 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
              <span>📊</span>
              <span>Data Diagnostics & Preprocessing</span>
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Review data quality, inspect statistical spreads, and configure cleaning rules
            </p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 border border-gray-250 rounded-xl text-xs font-bold text-gray-600 hover:bg-slate-50 transition-colors cursor-pointer"
            >
              ← Back
            </button>
          )}
        </div>

        {/* Loading State */}
        {isAnalyzing ? (
          <div className="p-16 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-250 border-t-blue-650"></div>
              <p className="text-xs font-semibold text-gray-500">Running statistical data analysis...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Data Diagnostics Summary Grid */}
            <div className="p-6 border-b border-gray-150 bg-slate-50/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider">Data Quality Summary</h3>
                <button
                  onClick={() => handleExplainWithAI('Data Diagnostics')}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer"
                >
                  <span>💡</span>
                  <span>Request AI features</span>
                </button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-white rounded-xl p-4 border border-gray-250/70 shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group cursor-default">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide group-hover:text-blue-500 transition-colors">Data Points</div>
                  <div className="text-2xl font-black text-gray-900 mt-1">{originalData.length}</div>
                  <div className="text-[10px] text-gray-500 mt-1.5 font-medium">Total time-series length</div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-250/70 shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group cursor-default">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide group-hover:text-blue-500 transition-colors">Stationary</div>
                  <div className={`text-2xl font-black mt-1 flex items-center space-x-1 ${stationarityData?.adf.p_value !== undefined && stationarityData.adf.p_value < 0.05 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    <span>{stationarityData?.adf.p_value !== undefined && stationarityData.adf.p_value < 0.05 ? 'Yes' : 'No'}</span>
                    {stationarityData?.adf.p_value !== undefined && (
                      <span className="text-[10px] font-normal text-gray-400">
                        (p={stationarityData.adf.p_value.toFixed(3)})
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1.5 font-medium">
                    {stationarityData?.adf.p_value !== undefined && stationarityData.adf.p_value < 0.05 
                      ? 'Stable mean & variance' 
                      : 'Requires differencing (d)'}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-250/70 shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group cursor-default">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide group-hover:text-blue-500 transition-colors">Quality Issues</div>
                  <div className={`text-2xl font-black mt-1 ${qualityIssues.length > 0 ? 'text-amber-600' : 'text-emerald-650'}`}>
                    {qualityIssues.length === 0 ? '0 Detected' : `${qualityIssues.length} Found`}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1.5 font-medium">
                    {qualityIssues.length === 0 ? 'Data is clean' : 'Requires preprocessing'}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-250/70 shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group cursor-default">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide group-hover:text-blue-500 transition-colors">Variation (CV)</div>
                  <div className="text-2xl font-black text-blue-600 mt-1">
                    {descriptiveStats ? `${descriptiveStats.cv.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1.5 font-medium">
                    {descriptiveStats
                      ? descriptiveStats.cv < 15
                        ? 'Low volatility (Stable)'
                        : descriptiveStats.cv < 30
                        ? 'Moderate volatility'
                        : 'High volatility'
                      : 'Spread of values'}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                className="text-xs text-blue-600 hover:text-blue-700 font-bold flex items-center space-x-1 cursor-pointer transition-colors"
              >
                <span>{showDetailedAnalysis ? '▲' : '▼'}</span>
                <span>{showDetailedAnalysis ? 'Hide' : 'Show'} Detailed Charts & Analytics</span>
              </button>
            </div>

            {/* Detailed Analysis - Collapsible */}
            {showDetailedAnalysis && (
              <>
                {/* Time Series Chart */}
                <div className="px-6 py-4 bg-white border-b border-gray-150">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">Time Series Overview</h4>
                      <p className="text-xs text-gray-500 mt-0.5">Historical visualization of target values over time</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Tabs */}
                      <div className="flex bg-gray-105 p-0.5 rounded-lg border border-gray-200">
                        <button
                          type="button"
                          onClick={() => setTsTab('chart')}
                          className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                            tsTab === 'chart'
                              ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                              : 'text-gray-500 hover:text-gray-900'
                          }`}
                        >
                          Chart View
                        </button>
                        <button
                          type="button"
                          onClick={() => setTsTab('explanation')}
                          className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                            tsTab === 'explanation'
                              ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                              : 'text-gray-500 hover:text-gray-900'
                          }`}
                        >
                          What is this?
                        </button>
                      </div>
                      <button
                        onClick={() => handleExplainWithAI('Time Series')}
                        className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer"
                      >
                        <span>💡</span>
                        <span>Request AI features</span>
                      </button>
                    </div>
                  </div>

                  {tsTab === 'chart' ? (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={originalData.slice(0, 100)}>
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            }}
                          />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{ fontSize: 12 }}
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          />
                          <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-gray-500 mt-2">
                        {originalData.length > 100 ? `Showing first 100 of ${originalData.length} data points` : `Showing all ${originalData.length} data points`}
                      </p>
                    </>
                  ) : (
                    <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
                      <p className="font-bold text-gray-800">Understanding Time Series Data</p>
                      <p>
                        A <strong>Time Series</strong> is a sequence of observations recorded chronologically at regular, consecutive intervals (e.g. hourly, daily, monthly).
                      </p>
                      <p>
                        Our browser-local engine analyzes this sequence to extract:
                      </p>
                      <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-500">
                        <li><strong>Trends:</strong> Long-term movements (upward or downward) in the values over time.</li>
                        <li><strong>Seasonality:</strong> Periodic fluctuations that repeat regularly (e.g., weekly retail peaks or holiday sales surges).</li>
                        <li><strong>Noise:</strong> Irregular random variations that cannot be explained by trend or seasonality.</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Distribution & Stats */}
                {descriptiveStats && (
                  <div className="px-6 py-4 bg-white border-b border-gray-150">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">Distribution & Statistics</h4>
                        <p className="text-xs text-gray-550 mt-0.5">Statistical spread and density of historical metrics</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        {/* Tabs */}
                        <div className="flex bg-gray-105 p-0.5 rounded-lg border border-gray-200">
                          <button
                            type="button"
                            onClick={() => setDistTab('histogram')}
                            className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                              distTab === 'histogram'
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                                : 'text-gray-500 hover:text-gray-900'
                            }`}
                          >
                            Histogram
                          </button>
                          <button
                            type="button"
                            onClick={() => setDistTab('stats')}
                            className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                              distTab === 'stats'
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                                : 'text-gray-500 hover:text-gray-900'
                            }`}
                          >
                            Stats Table
                          </button>
                          <button
                            type="button"
                            onClick={() => setDistTab('explanation')}
                            className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                              distTab === 'explanation'
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                                : 'text-gray-500 hover:text-gray-900'
                            }`}
                          >
                            What is this?
                          </button>
                        </div>
                        <button
                          onClick={() => handleExplainWithAI('Distribution')}
                          className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer"
                        >
                          <span>💡</span>
                          <span>Request AI features</span>
                        </button>
                      </div>
                    </div>

                    {distTab === 'histogram' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Value Distribution</div>
                          <div className="text-[10px] font-semibold text-gray-500">
                            X-axis: value range · Y-axis: count
                          </div>
                        </div>
                        <div className="h-64 bg-slate-50/50 rounded-xl border border-gray-200/80 px-3 pt-4 pb-2">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={histogramChartData} margin={{ top: 4, right: 12, left: 8, bottom: 34 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                              <XAxis
                                dataKey="range"
                                tick={{ fontSize: 10, fill: '#64748b' }}
                                interval={0}
                                angle={-25}
                                textAnchor="end"
                                height={54}
                                label={{ value: 'Value range', position: 'insideBottom', offset: -22, fontSize: 11, fill: '#475569' }}
                              />
                              <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 10, fill: '#64748b' }}
                                label={{ value: 'Count', angle: -90, position: 'insideLeft', offset: 6, fontSize: 11, fill: '#475569' }}
                              />
                              <Tooltip
                                formatter={(value) => [`${Number(value)} data points`, 'Count']}
                                labelFormatter={(label) => `Value range: ${label}`}
                                contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#e2e8f0' }}
                              />
                              <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {distTab === 'stats' && (
                      <div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-3">Key Descriptive Metrics</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Mean ± Std Dev</div>
                            <div className="text-sm font-bold text-gray-900 mt-1">
                              {descriptiveStats.mean.toFixed(1)} ± {descriptiveStats.stdDev.toFixed(1)}
                            </div>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Median</div>
                            <div className="text-sm font-bold text-gray-900 mt-1">
                              {descriptiveStats.median.toFixed(1)}
                            </div>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Range (Min - Max)</div>
                            <div className="text-sm font-bold text-gray-900 mt-1 text-[11px] sm:text-sm">
                              {descriptiveStats.min.toFixed(1)} - {descriptiveStats.max.toFixed(1)}
                            </div>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Volatility (CV)</div>
                            <div className={`text-sm font-bold mt-1 ${descriptiveStats.cv > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {descriptiveStats.cv.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {distTab === 'explanation' && (
                      <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
                        <p className="font-bold text-gray-800">Understanding Data Distribution & Volatility</p>
                        <p>
                          <strong>Value Distribution</strong> counts how many data points fall into different numerical bins. This chart indicates if your data spreads normally, shifts left/right, or contains extreme values (outliers).
                        </p>
                        <p>
                          The <strong>Coefficient of Variation (CV)</strong> shows volatility relative to your average values:
                        </p>
                        <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-505">
                          <li><strong>Low CV (&lt; 15%):</strong> Stable series. The mean is highly representative; predictions are typically reliable.</li>
                          <li><strong>High CV (&gt; 30%):</strong> Volatile series. Indicates significant spikes, trends, or noise. Advanced configurations (like Triple Exponential Smoothing) are recommended.</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Stationarity Tests */}
                {stationarityData && (
                  <div className="px-6 py-4 bg-white border-b border-gray-150">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">Stationarity Tests</h4>
                        <p className="text-xs text-gray-500 mt-0.5">Statistical checks for constant mean and variance parameters</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        {/* Tabs */}
                        <div className="flex bg-gray-105 p-0.5 rounded-lg border border-gray-200">
                          <button
                            type="button"
                            onClick={() => setStationarityTab('results')}
                            className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                              stationarityTab === 'results'
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                                : 'text-gray-500 hover:text-gray-900'
                          }`}
                        >
                          Test Results
                        </button>
                        <button
                          type="button"
                          onClick={() => setStationarityTab('explanation')}
                          className={`px-3 py-1 text-[11px] font-bold rounded-md transition-all cursor-pointer ${
                            stationarityTab === 'explanation'
                              ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50'
                              : 'text-gray-500 hover:text-gray-900'
                          }`}
                        >
                          What is Stationarity?
                        </button>
                      </div>
                      <button
                        onClick={() => handleExplainWithAI('Stationarity')}
                        className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-1 cursor-pointer"
                      >
                        <span>💡</span>
                        <span>Request AI features</span>
                      </button>
                    </div>
                  </div>

                    {stationarityTab === 'results' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">ADF (Augmented Dickey-Fuller) Test</div>
                          <div className={`text-sm font-bold ${stationarityData.adf.p_value < 0.05 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {stationarityData.adf.p_value < 0.05 ? 'Stationary (Constant Mean)' : 'Non-Stationary (Has Trend)'}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1 font-mono">p-value: {stationarityData.adf.p_value.toFixed(4)}</div>
                        </div>
                        <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">KPSS Test</div>
                          <div className={`text-sm font-bold ${stationarityData.kpss.is_stationary ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {stationarityData.kpss.is_stationary ? 'Stationary (Constant Variance)' : 'Non-Stationary (Variance Shifts)'}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1 font-mono">statistic: {stationarityData.kpss.statistic.toFixed(4)}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-gray-200/60 rounded-xl p-4 text-xs text-gray-600 leading-relaxed space-y-2">
                        <p className="font-bold text-gray-800">Understanding Time Series Stationarity</p>
                        <p>
                          A series is <strong>Stationary</strong> if its mean, variance, and autocorrelation structure do not change over time. Non-stationary data has trends or cycles that shift, which makes fitting statistical equations (like ARIMA) unstable.
                        </p>
                        <ul className="list-disc list-inside space-y-1 pl-1 text-[11px] text-gray-500">
                          <li><strong>ADF (Augmented Dickey-Fuller) Test:</strong> Tests for a unit root (trend). A p-value &lt; 0.05 indicates the data is stationary.</li>
                          <li><strong>KPSS Test:</strong> Tests the null hypothesis of stationarity. If the test statistic is below critical values, it confirms stationarity.</li>
                          <li><strong>Handling Non-Stationarity:</strong> If either test fails, the model configuration step will automatically apply <em>differencing (d)</em> to make the data stationary.</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Preprocessing - Only show if quality issues */}
            {qualityIssues.length > 0 && (
              <div className="px-6 py-5 bg-white border-t border-gray-150">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900">⚙️ Data Preprocessing Options</h3>
                  <button
                    onClick={() => handleExplainWithAI('Preprocessing')}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1 cursor-pointer"
                  >
                    <span>💡</span>
                    <span>Request AI features</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {qualityIssues.some(issue => issue.type === 'missing') && (
                    <div>
                      <label htmlFor="missing-value-method" className="block text-xs font-semibold text-gray-700 mb-2">
                        Missing Value Imputation
                        <span className="ml-1 text-yellow-600 font-bold">
                          ({qualityIssues.find(i => i.type === 'missing')?.count} missing)
                        </span>
                      </label>
                      <select
                        id="missing-value-method"
                        value={missingValueMethod}
                        onChange={e => setMissingValueMethod(e.target.value as any)}
                        className="w-full px-3 py-2 text-sm border border-gray-305 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="linear">Linear Interpolation</option>
                        <option value="forward_fill">Forward Fill</option>
                        <option value="backward_fill">Backward Fill</option>
                        <option value="remove">Remove Missing Rows</option>
                        <option value="none">Keep As-Is</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label htmlFor="outlier-handling" className="block text-xs font-semibold text-gray-700 mb-2">
                      Outlier Handling
                    </label>
                    <select
                      id="outlier-handling"
                      value={outlierHandling}
                      onChange={e => setOutlierHandling(e.target.value as any)}
                      className="w-full px-3 py-2 text-sm border border-gray-305 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="keep">Keep All Values</option>
                      <option value="cap_iqr">Cap at IQR Bounds</option>
                      <option value="cap_zscore">Cap at 3 Std Deviations</option>
                      <option value="remove">Remove Outliers</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Card 2: Model Configuration & Tuning */}
      {!isAnalyzing && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden transition-all duration-300">
          <div className="bg-white border-b border-gray-150 px-6 py-5">
            <h2 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
              <span>🤖</span>
              <span>Model Configuration & Tuning</span>
            </h2>
            <p className="text-xs text-gray-550 mt-0.5">
              Select model architecture, customize orders, scale inputs, and trigger local forecasts
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Recommendation Banner */}
            <div className={`p-4 rounded-xl border-2 ${
              recommendation.confidence === 'high'
                ? 'bg-emerald-50/50 border-emerald-200'
                : 'bg-blue-50/50 border-blue-200'
            }`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <span className="text-base">⭐</span>
                    <span className="text-sm font-bold text-gray-900">
                      Recommended: {recommendation.model === 'arima' ? 'ARIMA' : 'Exponential Smoothing'}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      recommendation.confidence === 'high'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {recommendation.confidence} confidence
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed">{recommendation.reason}</p>
                </div>
                <button
                  onClick={() => {
                    setConfig({ model_type: recommendation.model });
                    setAppliedRecommendation(true);
                    setTimeout(() => setAppliedRecommendation(false), 2000);
                  }}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm cursor-pointer whitespace-nowrap ${
                    appliedRecommendation
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white border border-gray-300 text-blue-600 hover:bg-slate-50'
                  }`}
                >
                  {appliedRecommendation ? '✓ Applied' : 'Apply Settings'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Model Selection & Params */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="model-type" className="block text-sm font-semibold text-gray-900 mb-2">
                    Model Type
                  </label>
                  <select
                    id="model-type"
                    value={config.model_type}
                    onChange={e => setConfig({ model_type: e.target.value as any })}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-205 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium cursor-pointer"
                  >
                    <option value="arima">ARIMA (AutoRegressive Integrated Moving Average)</option>
                    <option value="exponential_smoothing">Exponential Smoothing (Holt-Winters)</option>
                  </select>
                </div>

                {/* ARIMA Parameters */}
                {config.model_type === 'arima' && (
                  <div className="p-4 bg-slate-50/50 rounded-xl border border-gray-200/80 space-y-4">
                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">ARIMA Parameters (p, d, q)</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label htmlFor="arima-p" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                          p (AR order)
                        </label>
                        <input
                          id="arima-p"
                          type="number"
                          min="0"
                          max="5"
                          value={config.model_params?.p ?? 1}
                          onChange={e => setConfig({ model_params: { ...config.model_params, p: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="arima-d" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                          d (Diff order)
                        </label>
                        <input
                          id="arima-d"
                          type="number"
                          min="0"
                          max="2"
                          value={config.model_params?.d ?? 0}
                          onChange={e => setConfig({ model_params: { ...config.model_params, d: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="arima-q" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                          q (MA order)
                        </label>
                        <input
                          id="arima-q"
                          type="number"
                          min="0"
                          max="5"
                          value={config.model_params?.q ?? 1}
                          onChange={e => setConfig({ model_params: { ...config.model_params, q: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Exponential Smoothing Options */}
                {config.model_type === 'exponential_smoothing' && (
                  <div className="p-4 bg-slate-50/50 rounded-xl border border-gray-200/80 space-y-4">
                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Smoothing Components</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="es-trend" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                          Trend Component
                        </label>
                        <select
                          id="es-trend"
                          value={config.model_params?.type === 'simple' ? 'none' : 'add'}
                          onChange={e => setConfig({
                            model_params: {
                              ...config.model_params,
                              type: e.target.value === 'none' ? 'simple' : 'double',
                            },
                          })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer"
                        >
                          <option value="none">None</option>
                          <option value="additive">Additive</option>
                          <option value="multiplicative">Multiplicative</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="es-seasonal" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                          Seasonal Component
                        </label>
                        <select
                          id="es-seasonal"
                          value={config.model_params?.seasonal_type ?? 'none'}
                          onChange={e => setConfig({
                            model_params: {
                              ...config.model_params,
                              seasonal_type: e.target.value as any,
                              type: e.target.value === 'none' ? config.model_params?.type : 'triple',
                            },
                          })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer"
                        >
                          <option value="none">None</option>
                          <option value="add">Additive</option>
                          <option value="mul">Multiplicative</option>
                        </select>
                      </div>
                      {config.model_params?.seasonal_type && (config.model_params.seasonal_type as any) !== 'none' && (
                        <div className="col-span-2">
                          <label htmlFor="es-seasonal-periods" className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">
                            Seasonal Periods (Length of Cycle)
                          </label>
                          <input
                            id="es-seasonal-periods"
                            type="number"
                            min="2"
                            max="24"
                            value={config.model_params?.season_length ?? 12}
                            onChange={e => setConfig({
                              model_params: {
                                ...config.model_params,
                                season_length: parseInt(e.target.value) || 12,
                              },
                            })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Horizons, Transforms & Action */}
              <div className="space-y-6">
                {/* Forecast Horizon with Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="horizon" className="text-sm font-semibold text-gray-900">
                      Forecast Horizon
                    </label>
                    <span className="text-sm font-bold text-blue-650">{config.horizon} periods</span>
                  </div>
                  <input
                    id="horizon"
                    type="range"
                    min="1"
                    max="30"
                    value={config.horizon}
                    onChange={e => setConfig({ horizon: parseInt(e.target.value) })}
                    className="w-full h-2 bg-slate-100 border border-slate-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-[10px] font-bold text-gray-400 mt-1.5">
                    <span>1 Period</span>
                    <span>15 Periods</span>
                    <span>30 Periods</span>
                  </div>
                </div>

                {/* Confidence Level with Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="confidence" className="text-sm font-semibold text-gray-900">
                      Confidence Intervals
                    </label>
                    <span className="text-sm font-bold text-blue-650">{(config.confidence_level * 100).toFixed(0)}% Bounds</span>
                  </div>
                  <input
                    id="confidence"
                    type="range"
                    min="0.80"
                    max="0.99"
                    step="0.01"
                    value={config.confidence_level}
                    onChange={e => setConfig({ confidence_level: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-slate-100 border border-slate-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-[10px] font-bold text-gray-400 mt-1.5">
                    <span>80%</span>
                    <span>90%</span>
                    <span>95%</span>
                    <span>99%</span>
                  </div>
                </div>

                {/* Transformation */}
                <div>
                  <label htmlFor="transformation" className="block text-sm font-semibold text-gray-950 mb-2">
                    Variance-Stabilizing Transformation
                  </label>
                  <select
                    id="transformation"
                    value={config.preprocessing?.transformation ?? 'none'}
                    onChange={e => setConfig({ preprocessing: { ...config.preprocessing, transformation: e.target.value as any } })}
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-205 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium cursor-pointer transition-all duration-200"
                  >
                    <option value="none">None - Use original values</option>
                    <option value="log">Logarithmic (exp-growth, stabilizes variance, requires all values &gt; 0)</option>
                    <option value="sqrt">Square Root (stabilizes Poisson/count data variance, requires all values &ge; 0)</option>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    Variance-stabilization scales the values prior to modeling. Re-scaling is automatically inverted in results graphs.
                  </p>

                  {config.preprocessing?.transformation === 'log' && hasNonPositiveValues && (
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50/50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2 animate-fadeIn">
                      <span className="text-sm">⚠️</span>
                      <span>
                        <strong>Warning:</strong> Your data contains zero or negative values. A logarithmic transformation will fail during forecast run.
                      </span>
                    </div>
                  )}
                  {config.preprocessing?.transformation === 'sqrt' && hasNegativeValues && (
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50/50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2 animate-fadeIn">
                      <span className="text-sm">⚠️</span>
                      <span>
                        <strong>Warning:</strong> Your data contains negative values. A square root transformation will fail during forecast run.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Run Forecast Action Button */}
            <div className="pt-6 border-t border-gray-150 flex justify-end">
              <button
                onClick={handleRunForecast}
                disabled={isAnalyzing}
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-bold text-sm bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/10 hover:shadow-lg hover:shadow-blue-500/20 active:scale-98 transition-all duration-200 disabled:bg-gray-200 disabled:text-gray-405 disabled:shadow-none cursor-pointer flex items-center justify-center space-x-2"
              >
                <span>Run Forecasting Model</span>
                <span>→</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
